/**
 * Helper methods that translate custom validation errors into errors that RJSF expects.
 */
import {
  createErrorHandler,
  getDefaultFormState,
  toErrorSchema,
  unwrapErrorHandler,
  validationDataMerge,
} from '@rjsf/utils';

/**
 * Transforms validation errors that the `@cfworker/json-schema` library generates into
 * errors that the RJSF library expects.
 *
 * @param errors - error-like objects produced by the `@cfworker/json-schema` library
 */
export function transformRJSFValidationErrors(errors = []) {
  // Excludes all generic errors that precede more specific errors.
  // Includes generic errors that are missing a required property.
  // Includes all other errors.
  return errors
    .filter((err) => err.instanceLocation !== '#' || err.keyword === 'required')
    .map((err) => {
      return {
        name: err.keyword,
        property: extractFailedProperty(err),
        message: err.error,
        params: err.params || {},
        stack: generateStackMessage(err),
        schemaPath: err.instanceLocation,
      };
    });
}

/**
 * Extracts the invalid JSON property where possible from the `@cfworker/json-schema`
 * error format. See: https://www.npmjs.com/package/@cfworker/json-schema
 *
 * @param validationFailure - error-like object containing validation failure data
 */
function extractFailedProperty(validationFailure) {
  const { instanceLocation = '', error, keyword } = validationFailure;
  const locationPrefixRegex = /^#\/?/;
  const normalizedLocation = instanceLocation.replace(locationPrefixRegex, '');

  switch (keyword) {
    case 'required':
      const propertyRegex = /Instance does not have required property "(.*?)"/;
      const maybe_property = error.match(propertyRegex)?.[1] ?? '';
      return `${normalizedLocation}.${maybe_property}`;

    default:
      return decodeURI(normalizedLocation);
  }
}

/**
 * Transforms values from the `@cfworker/json-schema` data format into a stack message
 * format that RJSF expects.
 *
 * @param validationFailure - error-like object containing validation failure data
 */
function generateStackMessage(validationFailure) {
  const { keywordLocation = '', error: errorMsg } = validationFailure;
  const keywordPrefixRegex = /^#\/?.*required$/;
  const normalizedLocation = keywordLocation.replace(keywordPrefixRegex, '');

  return `${normalizedLocation} ${errorMsg}`.trim();
}

/**
 * --------------------------------------------------------------------------------------
 * SECTION NOTE:
 * All code in the following section has been duplicated and modified as needed from
 * the original source.
 *
 * LICENSE:
 * Apache License, Version 2.0 (Apache-2.0)
 * - https://github.com/rjsf-team/react-jsonschema-form/blob/v5.17.1/LICENSE.md
 * - https://www.apache.org/licenses/LICENSE-2.0
 *
 * COPYRIGHT:
 * The original source code is presumed to be the copyrighted works of the `rjsf-team`.
 * No formal copyright notice was posted with the source code at the time this module
 * was created. See: https://github.com/rjsf-team/react-jsonschema-form/tree/5.17.1
 *
 * NOTICE FILE:
 * No `NOTICE` file was posted with the source code at the time this module was created.
 * See: https://github.com/rjsf-team/react-jsonschema-form/tree/5.17.1
 *
 * STATEMENT OF CHANGES:
 * The following code has been converted from TypeScript to JavaScript and formatted
 * using an auto-formatter library.
 * --------------------------------------------------------------------------------------
 */

/**
 * This function processes the `formData` with an optional user contributed `customValidate` function, which receives
 * the form data and an `errorHandler` function that will be used to add custom validation errors for each field. Also
 * supports a `transformErrors` function that will take the raw validation errors, prior to custom validation, and
 * transform them in whatever way it chooses.
 *
 * See: https://github.com/rjsf-team/react-jsonschema-form/blob/v5.17.1/packages/validator-ajv8/src/processRawValidationErrors.ts
 *
 * @param validator - The `ValidatorType` implementation used for the `getDefaultFormState()` call
 * @param rawErrors - The list of raw `ErrorObject`s to process
 * @param formData - The form data to validate
 * @param schema - The schema against which to validate the form data
 * @param [customValidate] - An optional function that is used to perform custom validation
 * @param [transformErrors] - An optional function that is used to transform errors after validation
 * @param [uiSchema] - An optional uiSchema that is passed to `transformErrors` and `customValidate`
 */
export function processRawValidationErrors(
  validator,
  rawErrors,
  formData,
  schema,
  customValidate,
  transformErrors,
  uiSchema,
) {
  const { validationError: invalidSchemaError } = rawErrors;
  let errors = transformRJSFValidationErrors(rawErrors.errors, uiSchema);

  if (invalidSchemaError) {
    errors = [...errors, { stack: invalidSchemaError?.message }];
  }

  if (typeof transformErrors === 'function') {
    errors = transformErrors(errors, uiSchema);
  }

  let errorSchema = toErrorSchema(errors);

  if (invalidSchemaError) {
    errorSchema = {
      ...errorSchema,
      $schema: {
        __errors: [invalidSchemaError?.message],
      },
    };
  }

  if (typeof customValidate !== 'function') {
    return { errors, errorSchema };
  }

  // Include form data with undefined values, which is required for custom validation.
  const newFormData = getDefaultFormState(
    validator,
    schema,
    formData,
    schema,
    true,
  );

  const errorHandler = customValidate(
    newFormData,
    createErrorHandler(newFormData),
    uiSchema,
  );
  const userErrorSchema = unwrapErrorHandler(errorHandler);
  return validationDataMerge({ errors, errorSchema }, userErrorSchema);
}
